import { Box } from '@mui/material';
import { FavoriteIcon } from 'components/icons';
import { throttle } from 'lodash';
import React, { ReactElement, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { Event, useEventsApi } from 'services/api';
import { isStarredState } from 'state/starred-events/starred-events.state';
import { mutate } from 'swr';

export interface FavoriteEventButtonProps {
  eventId: Event['id'];
  children?: React.ReactNode;
  icon?: React.ComponentType<any>;
  gap?: number;
}

export const FavoriteEventButton = ({
  eventId,
  children,
  icon: CustomIcon,
  gap,
}: FavoriteEventButtonProps): ReactElement => {
  const [isStarred, setStarred] = useRecoilState<boolean>(
    isStarredState(eventId),
  );
  const eventsApi = useEventsApi();

  const toggleStarred = useCallback(
    throttle(
      async (isStarred: boolean) => {
        setStarred(!isStarred);
        if (isStarred) await eventsApi.unstarEvent(eventId);
        else await eventsApi.starEvent(eventId);

        void mutate('/events/starred');
      },
      80,
      { trailing: false },
    ),
    [],
  );

  const handleClickFavorite = () => {
    void toggleStarred(isStarred);
  };

  return (
    <Box
      lineHeight={0}
      sx={{
        cursor: 'pointer',
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
        display: 'flex',
        alignItems: 'center',
        ...(gap !== undefined && { gap }),
      }}
      className="keyboardFocus"
      onClick={handleClickFavorite}
      tabIndex={0}
      onKeyPress={(e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          void handleClickFavorite();
        }
      }}
    >
      {CustomIcon ? (
        <CustomIcon selected={isStarred} />
      ) : (
        <FavoriteIcon selected={isStarred} />
      )}
      {children}
    </Box>
  );
};
